import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {
    attachDirectionHint,
    applySplitDirectionAxes,
    createBoundedAxisTooltip,
    finalizeSplitDirectionChart,
    formatPercentAxis,
    formatTimeTick,
} from "../../helpers/chart_common";
import {isRedacted} from "../../helpers/redact";
import {getTopLevelItems, parseShaperChildrenBlob} from "../../helpers/shaper_children";
import {debugLog} from '../../helpers/debug_log';

export class ShaperCombinedRetransmits extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
    }

    title() {
        return "Shaper Child TCP Retransmits";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Shaper Combined Retransmits</h5><p>Child site TCP retransmits.</p>";
    }

    buildContainer() {
        let base = super.buildContainer();
        attachDirectionHint(base);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperCombinedRetransmitsGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return [ "SiteShaperPage" ];
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_children") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        const parsed = parseShaperChildrenBlob(msg.Data.blob);
        window.isFlatNetwork = parsed.isFlat;
        this.graph.update(parsed);
        this.lastGraphState = parsed;
    }

}

class ShaperCombinedRetransmitsGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxisPercent("% Retransmits", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option && this.chart.setOption(this.option);
    }

    update(payload) {
        this.chart.hideLoading();

        const dataset = getTopLevelItems(payload);
        const sites = dataset.topLevel || {};
        const topDownload = dataset.topDownload || [];
        const topUpload = dataset.topUpload || [];
        const topDownloadSet = new Set(topDownload);
        const topUploadSet = new Set(topUpload.length ? topUpload : topDownload);

        let series = [];
        let otherDown = [];
        let otherUp = [];
        let otherDownCounts = [];
        let otherUpCounts = [];

        const legends = new Set();
        this.option.legend.data = [];
        this.option.xAxis.data = [];

        let n = 0;
        const addLegend = (name) => {
            if (!legends.has(name)) {
                legends.add(name);
                this.option.legend.data.push(name);
            }
        };

        for (const [site, value] of Object.entries(sites)) {
            if (!this.option.xAxis.data.length) {
                this.option.xAxis.data = value.body.ticks || [];
            }
            const includeDown = !topDownloadSet.size || topDownloadSet.has(site);
            const includeUp = !topUploadSet.size || topUploadSet.has(site);
            const name = value.header.name;

            const downSeries = (dataset.isFlat ? value.body.tcp_retransmits_down_percent : value.body.retransmits_down_percent) || [];
            const upSeries = (dataset.isFlat ? value.body.tcp_retransmits_up_percent : value.body.retransmits_up_percent) || [];

            if (includeDown) {
                const d = downSeries.map((v) => (v || 0) / 100);
                series.push({
                    name: name,
                    type: "line",
                    data: d,
                    itemStyle: {
                        color: window.graphPalette[n]
                    },
                    smooth: true,
                    showSymbol: false,
                    xAxisIndex: 0,
                    yAxisIndex: 0,
                });
                addLegend(name);
            } else {
                for (let i=0; i<downSeries.length; i++) {
                    if (otherDown.length <= i) {
                        otherDown.push(0);
                        otherDownCounts.push(0);
                    }
                    if (downSeries[i] !== null && downSeries[i] !== undefined) {
                        otherDown[i] += (downSeries[i] || 0) / 100;
                        otherDownCounts[i] += 1;
                    }
                }
            }

            if (includeUp) {
                const d = upSeries.map((v) => 0 - ((v || 0) / 100));
                series.push({
                    name: name,
                    type: "line",
                    data: d,
                    itemStyle: {
                        color: window.graphPalette[n]
                    },
                    smooth: true,
                    showSymbol: false,
                    xAxisIndex: 1,
                    yAxisIndex: 0,
                });
                addLegend(name);
            } else {
                for (let i=0; i<upSeries.length; i++) {
                    if (otherUp.length <= i) {
                        otherUp.push(0);
                        otherUpCounts.push(0);
                    }
                    if (upSeries[i] !== null && upSeries[i] !== undefined) {
                        otherUp[i] += (upSeries[i] || 0) / 100;
                        otherUpCounts[i] += 1;
                    }
                }
            }
            if (includeDown || includeUp) {
                n += 1;
            }
        }

        // Don't render an aggregated "Other" line for retransmits. Summaries of
        // percentage metrics are misleading here and can dominate the axis scale.

        this.option.series = series;
        applySplitDirectionAxes(this.option, {
            ticks: this.option.xAxis.data,
            topLabel: "Download",
            bottomLabel: "Upload",
            yFormatter: formatPercentAxis,
        });
        
        // Apply redaction to legend if enabled
        if (isRedacted()) {
            this.option.legend.textStyle = {
                fontFamily: "Illegible"
            };
        } else {
            this.option.legend.textStyle = {};
        }
        
        this.option.tooltip = createBoundedAxisTooltip({
            linkAllXAxes: true,
            yFormatter: formatPercentAxis,
            formatter: (params) => {
                let result = `<div style="font-size: 10px;">${formatTimeTick(params[0].axisValue)}<br/>`;
                const downloadData = params.filter((p) => p.axisIndex === 0);
                const uploadData = params.filter((p) => p.axisIndex === 1);
                if (downloadData.length > 0) {
                    result += "<strong>Download retransmits</strong><br/>";
                    downloadData.forEach((item) => {
                        result += `<span style="color:${item.color}">●</span> ${item.seriesName}: ${formatPercentAxis(item.value, 2)}<br/>`;
                    });
                }
                if (uploadData.length > 0) {
                    result += "<strong>Upload retransmits</strong><br/>";
                    uploadData.forEach((item) => {
                        result += `<span style="color:${item.color}">●</span> ${item.seriesName}: ${formatPercentAxis(item.value, 2)}<br/>`;
                    });
                }
                result += "</div>";
                return result;
            },
        });

        // Add axis pointer configuration for highlighting
        this.option.axisPointer = {
            link: {xAxisIndex: 'all'},
            label: {
                backgroundColor: 'rgba(30,30,30,0.92)',
                color: '#fff',
                fontFamily: 'inherit'
            }
        };
        
        finalizeSplitDirectionChart(this.option);
        debugLog(this.option);
        this.option && this.chart.setOption(this.option, true);

        debugLog(payload);
    }
}
